function gcdLocal(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

export class Fraction {
  readonly num: number;
  readonly den: number;

  constructor(num: number, den: number) {
    if (!Number.isInteger(num)) throw new TypeError('Fraction numerator must be an integer');
    if (!Number.isInteger(den)) throw new TypeError('Fraction denominator must be an integer');
    if (den === 0) throw new Error('Fraction denominator must be non-zero');
    let n = num;
    let d = den;
    if (d < 0) {
      n = -n;
      d = -d;
    }
    const g = gcdLocal(n, d);
    this.num = g === 0 ? 0 : n / g;
    this.den = g === 0 ? 1 : d / g;
  }

  static readonly ZERO = new Fraction(0, 1);
  static readonly ONE = new Fraction(1, 1);

  static from(value: number, tolerance: number = 1e-6): Fraction {
    if (Number.isInteger(value)) return new Fraction(value, 1);
    const sign = value < 0 ? -1 : 1;
    const v = Math.abs(value);
    let lowerN = 0;
    let lowerD = 1;
    let upperN = 1;
    let upperD = 0;
    for (let i = 0; i < 1000; i += 1) {
      const midN = lowerN + upperN;
      const midD = lowerD + upperD;
      const mid = midN / midD;
      if (Math.abs(mid - v) < tolerance) {
        return new Fraction(sign * midN, midD);
      }
      if (mid < v) {
        lowerN = midN;
        lowerD = midD;
      } else {
        upperN = midN;
        upperD = midD;
      }
    }
    return new Fraction(sign * (lowerN + upperN), lowerD + upperD);
  }

  static parse(s: string): Fraction {
    const trimmed = s.trim();
    const mixed = trimmed.match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
    if (mixed && mixed[1] && mixed[2] && mixed[3]) {
      const whole = parseInt(mixed[1], 10);
      const n = parseInt(mixed[2], 10);
      const d = parseInt(mixed[3], 10);
      const sign = whole < 0 ? -1 : 1;
      return new Fraction(sign * (Math.abs(whole) * d + n), d);
    }
    const simple = trimmed.match(/^(-?\d+)\/(-?\d+)$/);
    if (simple && simple[1] && simple[2]) {
      return new Fraction(parseInt(simple[1], 10), parseInt(simple[2], 10));
    }
    const integer = trimmed.match(/^-?\d+$/);
    if (integer) {
      return new Fraction(parseInt(trimmed, 10), 1);
    }
    throw new Error(`Fraction.parse: cannot parse "${s}"`);
  }

  add(other: Fraction): Fraction {
    return new Fraction(this.num * other.den + other.num * this.den, this.den * other.den);
  }

  sub(other: Fraction): Fraction {
    return new Fraction(this.num * other.den - other.num * this.den, this.den * other.den);
  }

  mul(other: Fraction): Fraction {
    return new Fraction(this.num * other.num, this.den * other.den);
  }

  div(other: Fraction): Fraction {
    if (other.num === 0) throw new Error('Fraction.div: division by zero');
    return new Fraction(this.num * other.den, this.den * other.num);
  }

  neg(): Fraction {
    return new Fraction(-this.num, this.den);
  }

  reciprocal(): Fraction {
    if (this.num === 0) throw new Error('Fraction.reciprocal: cannot invert zero');
    return new Fraction(this.den, this.num);
  }

  toNumber(): number {
    return this.num / this.den;
  }

  toString(): string {
    if (this.den === 1) return String(this.num);
    return `${this.num}/${this.den}`;
  }

  toMixedString(): string {
    if (this.den === 1) return String(this.num);
    if (Math.abs(this.num) < this.den) return this.toString();
    const sign = this.num < 0 ? -1 : 1;
    const absNum = Math.abs(this.num);
    const whole = Math.floor(absNum / this.den);
    const remainder = absNum % this.den;
    if (remainder === 0) return String(sign * whole);
    return `${sign * whole} ${remainder}/${this.den}`;
  }

  toDecimalString(precision: number = 6, locale: string = 'en'): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(this.toNumber());
  }

  toPercentString(precision: number = 0): string {
    return new Intl.NumberFormat('en', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(this.toNumber());
  }

  isInteger(): boolean {
    return this.den === 1;
  }

  isImproper(): boolean {
    return Math.abs(this.num) >= this.den;
  }

  equals(other: Fraction): boolean {
    return this.num === other.num && this.den === other.den;
  }

  cmp(other: Fraction): -1 | 0 | 1 {
    const diff = this.num * other.den - other.num * this.den;
    if (diff < 0) return -1;
    if (diff > 0) return 1;
    return 0;
  }

  get sign(): -1 | 0 | 1 {
    if (this.num < 0) return -1;
    if (this.num > 0) return 1;
    return 0;
  }

  get abs(): Fraction {
    return new Fraction(Math.abs(this.num), this.den);
  }

  pow(exp: number): Fraction {
    if (!Number.isInteger(exp)) throw new TypeError('Exponent must be an integer');
    if (exp < 0) {
      return this.reciprocal().pow(-exp);
    }
    let result = Fraction.ONE;
    for (let i = 0; i < exp; i += 1) {
      result = result.mul(this);
    }
    return result;
  }
}
